import { useState, useCallback } from 'react';
import { PhaseIndicator } from './PhaseIndicator';
import { PhaseNames } from './PhaseNames';
import { PhaseMessages } from './PhaseMessages';
import { PhasePhones } from './PhasePhones';
import { PhaseDetails } from './PhaseDetails';
import { PhaseResponses } from './PhaseResponses';
import type { ReferidoFormData, ReferidoPhase } from '@/types';

interface ReferidoPhaseFlowProps {
  referidos: ReferidoFormData[];
  setReferidos: React.Dispatch<React.SetStateAction<ReferidoFormData[]>>;
  onComplete: () => void;
}

const INITIAL_REFERIDO: ReferidoFormData = {
  nombre: '',
  apellido: '',
  telefono: '',
  ciudad: '',
  // Sin default: el asesor elige la relación a conciencia ("Seleccionar...").
  relacion: '' as unknown as ReferidoFormData['relacion'],
  trabaja: 'NO',
  dato_extra: '',
};

export function ReferidoPhaseFlow({
  referidos,
  setReferidos,
  onComplete,
}: ReferidoPhaseFlowProps) {
  const [currentPhase, setCurrentPhase] = useState<ReferidoPhase>('names');
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleAddReferido = useCallback(
    (nombre: string, apellido: string) => {
      setReferidos((prev) => [
        ...prev,
        { ...INITIAL_REFERIDO, nombre, apellido },
      ]);
    },
    [setReferidos]
  );

  const handleRemoveReferido = useCallback(
    (index: number) => {
      setReferidos((prev) => prev.filter((_, i) => i !== index));
    },
    [setReferidos]
  );

  const handleUpdateReferido = useCallback(
    (index: number, updates: Partial<ReferidoFormData>) => {
      setReferidos((prev) =>
        prev.map((r, i) => (i === index ? { ...r, ...updates } : r))
      );
    },
    [setReferidos]
  );

  const handleNavigate = useCallback((index: number) => {
    if (index >= 0) {
      setCurrentIndex(index);
    }
  }, []);

  const goToPhase = (phase: ReferidoPhase) => {
    setCurrentPhase(phase);
    setCurrentIndex(0);
  };

  return (
    <div className="space-y-4">
      {/* Phase indicator */}
      <PhaseIndicator currentPhase={currentPhase} />

      {/* Phase 1: Names */}
      {currentPhase === 'names' && (
        <PhaseNames
          referidos={referidos}
          onAddReferido={handleAddReferido}
          onRemoveReferido={handleRemoveReferido}
          onContinue={() => goToPhase('messages')}
        />
      )}

      {/* Phase 2: Messages — select send method only */}
      {currentPhase === 'messages' && (
        <PhaseMessages
          referidos={referidos}
          currentIndex={currentIndex}
          onUpdateReferido={handleUpdateReferido}
          onNavigate={handleNavigate}
          onContinue={() => goToPhase('phones')}
          onBack={() => goToPhase('names')}
        />
      )}

      {/* Phase 3: Phones */}
      {currentPhase === 'phones' && (
        <PhasePhones
          referidos={referidos}
          currentIndex={currentIndex}
          onUpdateReferido={handleUpdateReferido}
          onNavigate={handleNavigate}
          onContinue={() => goToPhase('details')}
          onBack={() => goToPhase('messages')}
        />
      )}

      {/* Phase 4: Details */}
      {currentPhase === 'details' && (
        <PhaseDetails
          referidos={referidos}
          currentIndex={currentIndex}
          onUpdateReferido={handleUpdateReferido}
          onNavigate={handleNavigate}
          onComplete={() => goToPhase('responses')}
          onBack={() => goToPhase('phones')}
        />
      )}

      {/* Phase 5: Responses — who replied to the message? */}
      {currentPhase === 'responses' && (
        <PhaseResponses
          referidos={referidos}
          currentIndex={currentIndex}
          onUpdateReferido={handleUpdateReferido}
          onNavigate={handleNavigate}
          onComplete={onComplete}
          onBack={() => goToPhase('details')}
        />
      )}
    </div>
  );
}
